/**
 * The single focused row of the daily outreach ritual — the note, the wait,
 * or the message, whichever the current rung's row actually has to show.
 *
 * On LinkedIn you cannot message a non-connection: a connection request
 * (with a note capped at ~300 characters) has to be sent and accepted before
 * the real message can go out. `row.state` decides which artifact this
 * renders — never more than one at a time:
 *
 *   needs_connection  -> `connection_note` (may be null) + two distinct sends
 *   connection_sent   -> nothing to show; only the wait and a decision
 *   connected         -> `message_text` (+ `followup_text`), Copy, then send
 *
 * `connection_note` and `message_text` already arrive with the row (the
 * browser's own SELECT reads them — see useOutreach.ts's useOutreachQueue),
 * so nothing here is gated behind a reveal step the way the old card gated
 * it. The one place the unchanged `crm_outreach_finalize` RPC still checks a
 * live claim is its final `sent` branch (recording the real message as sent
 * creates or touches a CRM contact), so that is the only path that still
 * calls `crm_outreach_claim` (via outreach-claim.ts) first — right before
 * that decision, not as a separate manual step.
 *
 * Per-rung action sets follow the task spec's explicit lists exactly, including
 * where they narrow. The profile link is deliberately NOT one of them. It sits
 * in the header on every rung, in the same place, because it is not an action
 * belonging to a step — it is the anchor to the human. The first version made
 * it a needs_connection action and the owner hit that within minutes of real
 * use: on connection_sent the entire job is "open their profile and see whether
 * they accepted", and that was exactly where the link had disappeared.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Clipboard,
  ExternalLink,
  UserCheck,
  UserPlus,
  UserX,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useClaimOutreach, useFinalizeOutreach } from '@/hooks/crm/useOutreach';
import {
  CONNECTION_NOTE_CHAR_LIMIT,
  describeWaitDuration,
  outreachDisplayName,
  waitUrgency,
  type OutreachRow,
  type WaitUrgency,
} from '@/lib/crm/outreach';
import { cn } from '@/lib/utils';

export interface OutreachCardHandle {
  /** Enter — the one accented action for this rung, where one exists. */
  triggerPrimary: () => void;
  /** s — skip, same as clicking the Skip button (needs_connection / connection_sent only). */
  triggerSkip: () => void;
  /** o — open profile. Available on every rung; the link is always in the header. */
  openProfile: () => void;
}

interface OutreachCardProps {
  row: OutreachRow;
  /** A claim came back 409 (someone else holds it): refresh the queue list. */
  onClaimConflict: () => void;
  /** So the page can disable j/k/Enter/o/s while a dialog owns focus. */
  onDialogOpenChange: (open: boolean) => void;
}

type ActiveDialog = 'sent' | 'skip' | null;

interface SentFormValues {
  name: string;
  company: string;
  sentOn: string;
  note: string;
}

const sentFormSchema = z.object({
  name: z.string().max(255),
  company: z.string().max(255),
  sentOn: z.string(),
  note: z.string().max(2000),
});

interface SkipFormValues {
  reason: string;
}

const skipFormSchema = z.object({
  reason: z.string().trim().min(1, 'A reason is required to skip this row.').max(500),
});

const WAIT_URGENCY_CLASS: Record<WaitUrgency, string> = {
  quiet: 'text-muted-foreground',
  normal: 'text-foreground',
  stale: 'text-status-warning',
};

function todayDateKey(): string {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function SentDialog({
  row,
  isPending,
  onClose,
  onSubmit,
}: {
  row: OutreachRow;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (values: SentFormValues) => Promise<void>;
}): JSX.Element {
  const { register, handleSubmit } = useForm<SentFormValues>({
    resolver: zodResolver(sentFormSchema),
    defaultValues: {
      name: [row.first_name, row.last_name].filter(Boolean).join(' ').trim(),
      company: row.company_name ?? '',
      sentOn: todayDateKey(),
      note: '',
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark as sent</DialogTitle>
          <DialogDescription>
            Records that you sent this message from your own LinkedIn account. Tacit never
            sends a message itself.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((values) => void onSubmit(values))} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="outreach-sent-name">Name</Label>
              <Input id="outreach-sent-name" {...register('name')} data-content-origin="external" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="outreach-sent-company">Company</Label>
              <Input id="outreach-sent-company" {...register('company')} data-content-origin="external" />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="outreach-sent-on">Sent on</Label>
            <Input id="outreach-sent-on" type="date" {...register('sentOn')} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="outreach-sent-note">Note (optional)</Label>
            <Textarea
              id="outreach-sent-note"
              rows={3}
              {...register('note')}
              placeholder="Context for whoever reads this contact's timeline next."
              data-content-origin="user"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-11 md:min-h-11"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button type="submit" className="min-h-11 md:min-h-11" disabled={isPending}>
              {isPending ? 'Saving…' : 'Confirm sent'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SkipDialog({
  isPending,
  onClose,
  onSubmit,
}: {
  isPending: boolean;
  onClose: () => void;
  onSubmit: (values: SkipFormValues) => Promise<void>;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SkipFormValues>({
    resolver: zodResolver(skipFormSchema),
    defaultValues: { reason: '' },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Skip this row</DialogTitle>
          <DialogDescription>
            The row leaves the queue for good. Say why, for whoever reads this later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((values) => void onSubmit(values))} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="outreach-skip-reason">Reason</Label>
            <Textarea
              id="outreach-skip-reason"
              rows={3}
              {...register('reason')}
              placeholder="Why this row should not be sent."
              data-content-origin="user"
            />
            {errors.reason && (
              <p className="text-sm text-destructive" role="alert">{errors.reason.message}</p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-11 md:min-h-11"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              className="min-h-11 md:min-h-11"
              disabled={isPending}
            >
              {isPending ? 'Saving…' : 'Skip'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function IdentityLine({ row }: { row: OutreachRow }): JSX.Element {
  return (
    <div>
      <p className="break-words text-base font-semibold" data-content-origin="external">
        {outreachDisplayName(row)}
      </p>
      <p className="break-words text-sm text-muted-foreground" data-content-origin="external">
        {[row.job_title, row.company_name].filter(Boolean).join(' · ') || '—'}
      </p>
    </div>
  );
}

export const OutreachCard = forwardRef<OutreachCardHandle, OutreachCardProps>(
  function OutreachCard({ row, onClaimConflict, onDialogOpenChange }, ref) {
    const claim = useClaimOutreach();
    const finalize = useFinalizeOutreach();
    const [dialog, setDialog] = useState<ActiveDialog>(null);
    const profileLinkRef = useRef<HTMLAnchorElement>(null);

    useEffect(() => {
      onDialogOpenChange(dialog !== null);
    }, [dialog, onDialogOpenChange]);

    // A stale form from a different person should never linger once the
    // ritual moves focus on.
    useEffect(() => {
      setDialog(null);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [row.id]);

    const reportError = (error: unknown, fallback: string): void => {
      toast.error(error instanceof Error ? error.message : fallback);
    };

    const handleSkip = async (values: SkipFormValues): Promise<void> => {
      try {
        await finalize.mutateAsync({ id: row.id, decision: 'skipped', skippedReason: values.reason.trim() });
        toast.success('Skipped');
        setDialog(null);
      } catch (error) {
        reportError(error, 'This could not be recorded. Try again.');
      }
    };

    // ─── needs_connection ───
    const handleSendBareRequest = async (): Promise<void> => {
      try {
        await finalize.mutateAsync({ id: row.id, decision: 'connection_sent', noteUsed: false });
        toast.success('Recorded as sent');
      } catch (error) {
        reportError(error, 'This could not be recorded. Try again.');
      }
    };

    const handleSendWithNote = async (): Promise<void> => {
      if (row.connection_note) {
        try {
          if (!navigator.clipboard) throw new Error('Clipboard unavailable');
          await navigator.clipboard.writeText(row.connection_note);
        } catch {
          toast.error('Could not copy automatically. Select and copy the note text above.');
        }
      }
      // Quiet, not alarming: this is a normal, deliberate choice, not an error.
      toast('Personalised invites are limited on most plans.');

      try {
        await finalize.mutateAsync({ id: row.id, decision: 'connection_sent', noteUsed: true });
        toast.success('Recorded as sent with note');
      } catch (error) {
        reportError(error, 'This could not be recorded. Try again.');
      }
    };

    const handleAlreadyConnected = async (): Promise<void> => {
      try {
        await finalize.mutateAsync({ id: row.id, decision: 'connected' });
        toast.success('Marked as connected');
      } catch (error) {
        reportError(error, 'This could not be recorded. Try again.');
      }
    };

    // ─── connection_sent ───
    const handleAccepted = async (): Promise<void> => {
      try {
        await finalize.mutateAsync({ id: row.id, decision: 'connected' });
        toast.success('Marked as connected');
      } catch (error) {
        reportError(error, 'This could not be recorded. Try again.');
      }
    };

    const handleNoAnswer = async (): Promise<void> => {
      try {
        await finalize.mutateAsync({ id: row.id, decision: 'declined' });
        toast.success('Recorded as declined');
      } catch (error) {
        reportError(error, 'This could not be recorded. Try again.');
      }
    };

    // ─── connected ───
    // The claim is taken HERE, before the text reaches the clipboard, not later
    // when the send is recorded. The send is physical: once the operator has the
    // message they will paste it into LinkedIn, and no later check can call that
    // back. Claiming at record time would only ever prevent the second *row
    // update* while both operators had already sent the same message — which is
    // the defect a cross-model review raised as CRITICAL (F2) against the
    // original plan. A duplicate connection request is harmless (LinkedIn shows
    // it as already pending), so `needs_connection` deliberately does not gate;
    // a duplicate real message is not.
    const handleCopyMessage = async (): Promise<void> => {
      try {
        await claim.mutateAsync({ id: row.id });
      } catch (error) {
        if (error instanceof Error && error.name === 'OUTREACH_NOT_CLAIMABLE') {
          toast.error('Someone else is working on this');
          onClaimConflict();
          return;
        }
        reportError(error, 'This could not be claimed. Try again.');
        return;
      }
      try {
        if (!navigator.clipboard) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(row.message_text);
        toast.success('Message copied to clipboard');
      } catch {
        toast.error('Could not copy automatically. Select and copy the message text above.');
      }
    };

    const handleSent = async (values: SentFormValues): Promise<void> => {
      try {
        // Refresh the lease taken at copy time; the operator may have spent
        // longer in LinkedIn than the lease allows.
        await claim.mutateAsync({ id: row.id });
        await finalize.mutateAsync({
          id: row.id,
          decision: 'sent',
          name: values.name.trim() || undefined,
          company: values.company.trim() || undefined,
          sentOn: values.sentOn || undefined,
          note: values.note.trim() || undefined,
        });
        toast.success('Recorded as sent');
        setDialog(null);
      } catch (error) {
        if (error instanceof Error && error.name === 'OUTREACH_NOT_CLAIMABLE') {
          toast.error('Someone else is working on this');
          onClaimConflict();
          return;
        }
        reportError(error, 'This could not be recorded. Try again.');
      }
    };

    useImperativeHandle(ref, () => ({
      triggerPrimary: () => {
        if (dialog) return;
        if (row.state === 'needs_connection') void handleSendBareRequest();
        else if (row.state === 'connection_sent') void handleAccepted();
        else if (row.state === 'connected') setDialog('sent');
      },
      triggerSkip: () => {
        if (dialog) return;
        if (row.state === 'connected') return; // no Skip on this rung — see file header
        setDialog('skip');
      },
      openProfile: () => {
        profileLinkRef.current?.click();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [dialog, row.id, row.state]);

    const isBusy = finalize.isPending || claim.isPending;

    return (
      <section className="rounded-lg border border-border">
        {/*
          The profile link lives in the header, on EVERY rung, in the same place.
          It was originally an action inside the needs_connection block only, and
          the owner hit that within minutes of first use: on connection_sent the
          whole job is "go look at their profile and see whether they accepted",
          and the link had vanished exactly there. It is not a per-rung action —
          it is the anchor to the human, so it belongs beside their name.
        */}
        <div className="flex items-start justify-between gap-4 p-5 pb-4">
          <IdentityLine row={row} />
          <a
            ref={profileLinkRef}
            href={row.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            className="interactive inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground md:min-h-11"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            Open profile
          </a>
        </div>

        <div className="border-t border-border p-5">
          {row.state === 'needs_connection' && (
            row.connection_note ? (
              <>
                <p className="whitespace-pre-wrap text-lg leading-relaxed" data-content-origin="external">
                  {row.connection_note}
                </p>
                <Badge
                  variant={row.connection_note.length > CONNECTION_NOTE_CHAR_LIMIT ? 'destructive' : 'outline'}
                  className="mt-3"
                >
                  {row.connection_note.length} / {CONNECTION_NOTE_CHAR_LIMIT}
                  {row.connection_note.length > CONNECTION_NOTE_CHAR_LIMIT ? ' — over limit' : ''}
                </Badge>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No connection note written yet</p>
            )
          )}

          {row.state === 'connection_sent' && (
            <p className={cn('text-3xl font-semibold tracking-tight', WAIT_URGENCY_CLASS[waitUrgency(row.connection_sent_at)])}>
              {describeWaitDuration(row.connection_sent_at)}
            </p>
          )}

          {row.state === 'connected' && (
            <div className="space-y-3">
              <p className="whitespace-pre-wrap text-lg leading-relaxed" data-content-origin="external">
                {row.message_text}
              </p>
              {row.followup_text && (
                <div className="space-y-1 border-t border-border pt-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Follow-up
                  </p>
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground" data-content-origin="external">
                    {row.followup_text}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 border-t border-border p-5 pt-4">
          {row.state === 'needs_connection' && (
            <>
              <Button className="min-h-11 md:min-h-11" onClick={() => void handleSendBareRequest()} disabled={isBusy}>
                <UserPlus className="h-4 w-4" aria-hidden="true" />
                Send bare request
              </Button>
              {row.connection_note && (
                <Button
                  variant="outline"
                  className="min-h-11 md:min-h-11"
                  onClick={() => void handleSendWithNote()}
                  disabled={isBusy}
                >
                  <Clipboard className="h-4 w-4" aria-hidden="true" />
                  Send with note
                </Button>
              )}
              <Button
                variant="outline"
                className="min-h-11 md:min-h-11"
                onClick={() => void handleAlreadyConnected()}
                disabled={isBusy}
              >
                <UserCheck className="h-4 w-4" aria-hidden="true" />
                Already connected
              </Button>
              <Button variant="ghost" className="min-h-11 md:min-h-11" onClick={() => setDialog('skip')} disabled={isBusy}>
                <XCircle className="h-4 w-4" aria-hidden="true" />
                Skip
              </Button>
            </>
          )}

          {row.state === 'connection_sent' && (
            <>
              <Button className="min-h-11 md:min-h-11" onClick={() => void handleAccepted()} disabled={isBusy}>
                <UserCheck className="h-4 w-4" aria-hidden="true" />
                They accepted
              </Button>
              <Button variant="outline" className="min-h-11 md:min-h-11" onClick={() => void handleNoAnswer()} disabled={isBusy}>
                <UserX className="h-4 w-4" aria-hidden="true" />
                No answer
              </Button>
              <Button variant="ghost" className="min-h-11 md:min-h-11" onClick={() => setDialog('skip')} disabled={isBusy}>
                <XCircle className="h-4 w-4" aria-hidden="true" />
                Skip
              </Button>
            </>
          )}

          {row.state === 'connected' && (
            <>
              <Button variant="outline" className="min-h-11 md:min-h-11" onClick={() => void handleCopyMessage()}>
                <Clipboard className="h-4 w-4" aria-hidden="true" />
                Copy
              </Button>
              <Button className="min-h-11 md:min-h-11" onClick={() => setDialog('sent')}>
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                I sent it
              </Button>
            </>
          )}
        </div>

        {dialog === 'sent' && (
          <SentDialog
            row={row}
            isPending={finalize.isPending || claim.isPending}
            onClose={() => setDialog(null)}
            onSubmit={handleSent}
          />
        )}

        {dialog === 'skip' && (
          <SkipDialog
            isPending={finalize.isPending}
            onClose={() => setDialog(null)}
            onSubmit={handleSkip}
          />
        )}
      </section>
    );
  },
);
